/*
 * SEO KEYWORD MAP — CALCULADORA DE MINUTOS
 * URL: /calculadora-de-minutos
 * Primary: calculadora de minutos (3.6K vol, KD 16)
 * LSI: somar minutos, converter minutos em horas, minutos para horas,
 *       quantos minutos tem uma hora, calculadora de minutos online
 * Page type: Calculator Tool
 */
import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import JsonLd from '../components/JsonLd';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import AdSlot from '../components/AdSlot';
import FAQAccordion from '../components/FAQAccordion';
import RelatedTools from '../components/RelatedTools';
import { formatNumber } from '../utils/timeCalculations';

const CalculadoraMinutosPage = () => {
  const [mode, setMode] = useState('somar');
  const [entries, setEntries] = useState([{ id: 1, value: '' }, { id: 2, value: '' }, { id: 3, value: '' }]);
  const [convertVal, setConvertVal] = useState('');
  const [convertDir, setConvertDir] = useState('minToHours');

  const sumResult = useMemo(() => {
    if (mode !== 'somar') return null;
    const total = entries.reduce((sum, e) => sum + (parseFloat(e.value) || 0), 0);
    if (total === 0) return null;
    // Round to whole minutes before splitting, so 59.6 min never shows as "0h 60min".
    const roundedTotal = Math.round(total);
    const hours = Math.floor(roundedTotal / 60);
    const mins = roundedTotal % 60;
    return { total, hours, mins, decimal: formatNumber(total / 60, 4), formatted: `${hours}h ${mins}min` };
  }, [entries, mode]);

  const convertResult = useMemo(() => {
    if (mode !== 'converter') return null;
    const val = parseFloat(convertVal) || 0;
    if (val === 0) return null;
    if (convertDir === 'minToHours') {
      const rounded = Math.round(val);
      const h = Math.floor(rounded / 60);
      const m = rounded % 60;
      return { result: `${h}h ${m}min (${formatNumber(val / 60, 4)} horas decimais)`, detail: `${val} minutos = ${h} horas e ${m} minutos` };
    } else if (convertDir === 'hoursToMin') {
      return { result: `${(val * 60).toFixed(0)} minutos`, detail: `${val} horas = ${(val * 60).toFixed(0)} minutos` };
    } else {
      return { result: `${formatNumber(val / 60)} minutos`, detail: `${formatNumber(val, 0)} segundos = ${formatNumber(val / 60)} minutos` };
    }
  }, [convertVal, convertDir, mode]);

  const addEntry = () => setEntries([...entries, { id: Date.now(), value: '' }]);

  const conversionTable = [
    { min: 5, hours: '0h 5min', decimal: '0,08h' },
    { min: 10, hours: '0h 10min', decimal: '0,17h' },
    { min: 15, hours: '0h 15min', decimal: '0,25h' },
    { min: 20, hours: '0h 20min', decimal: '0,33h' },
    { min: 30, hours: '0h 30min', decimal: '0,50h' },
    { min: 45, hours: '0h 45min', decimal: '0,75h' },
    { min: 60, hours: '1h 0min', decimal: '1,00h' },
    { min: 90, hours: '1h 30min', decimal: '1,50h' },
    { min: 120, hours: '2h 0min', decimal: '2,00h' },
    { min: 150, hours: '2h 30min', decimal: '2,50h' },
    { min: 180, hours: '3h 0min', decimal: '3,00h' },
    { min: 480, hours: '8h 0min', decimal: '8,00h' },
    { min: 528, hours: '8h 48min', decimal: '8,80h' },
  ];

  const faqs = [
    { question: 'Como somar minutos?', answer: 'Para somar minutos, insira cada valor no campo correspondente e a calculadora soma automaticamente. Se o total ultrapassar 60, ela converte para horas e minutos. Por exemplo, 45 + 30 = 75 minutos = 1 hora e 15 minutos.' },
    { question: 'Como converter minutos em horas?', answer: 'Divida os minutos por 60. O resultado inteiro são as horas e o resto são os minutos. Por exemplo: 150 minutos ÷ 60 = 2 horas e 30 minutos (2,50 horas decimais). Nossa calculadora faz isso automaticamente.' },
    { question: '30 minutos são quantas horas em decimal?', answer: '30 minutos equivalem a 0,50 horas em formato decimal (30 ÷ 60 = 0,50). Cuidado: 30 minutos NÃO são 0,30 horas — esse é um erro muito comum.' },
    { question: 'Quantos minutos tem uma hora?', answer: 'Uma hora tem exatamente 60 minutos, ou 3.600 segundos. Esse é o padrão do sistema sexagesimal de medida de tempo.' },
    { question: '45 minutos são quantas horas?', answer: '45 minutos equivalem a 0,75 horas em formato decimal (45 ÷ 60 = 0,75). No formato HH:MM, é representado como 0:45.' },
    { question: 'Para que serve a calculadora de minutos?', answer: 'A calculadora de minutos serve para somar durações em minutos (como tempos de tarefas, exercícios ou viagens), converter minutos em horas e vice-versa, e obter resultados em formato decimal para folha de pagamento.' }
  ];

  const structuredData = { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqs.map(faq => ({ "@type": "Question", "name": faq.question, "acceptedAnswer": { "@type": "Answer", "text": faq.answer } })) };

  return (
    <>
      <Helmet>
        <html lang="pt-BR" />
        <title>Calculadora de Minutos: Somar e Converter em Horas</title>
        <meta name="description" content="Calculadora de minutos online grátis. Some minutos, converta minutos em horas e veja o resultado em decimal. Tabela de conversão completa." />
        <link rel="canonical" href="https://calculadoradehoras.net.br/calculadora-de-minutos" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Calculadora de Minutos: Somar e Converter em Horas" />
        <meta property="og:description" content="Some minutos e converta para horas. Resultado em HH:MM e decimal. Grátis." />
        <meta property="og:url" content="https://calculadoradehoras.net.br/calculadora-de-minutos" />
        <meta property="og:image" content="https://calculadoradehoras.net.br/og-image.png" />
        <meta property="og:site_name" content="Calculadora de Horas" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Calculadora de Minutos: Somar e Converter em Horas" />
        <meta name="twitter:description" content="Some minutos e converta para horas. Resultado em HH:MM e decimal. Grátis." />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
      </Helmet>
      <JsonLd data={structuredData} />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        "itemListElement": [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://calculadoradehoras.net.br/"},
          {"@type":"ListItem","position":2,"name":"Calculadora de Minutos","item":"https://calculadoradehoras.net.br/calculadora-de-minutos"}
        ]
      }} />

      <Navigation />

      <main className="max-w-4xl mx-auto px-4 py-8">
        <nav className="text-sm text-body-text mb-4">
          <a href="/" className="hover:text-blue-primary">Início</a><span className="mx-2">›</span><span>Calculadora de Minutos</span>
        </nav>

        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-dark-text mb-3">Calculadora de Minutos Online</h1>
          <p className="text-lg text-body-text max-w-2xl mx-auto">Some minutos, converta para horas e obtenha resultados em formato HH:MM e decimal. Ferramenta gratuita e sem cadastro.</p>
        </div>

        {/* Calculator */}
        <div className="bg-white rounded-xl border-2 border-border-gray p-6 md:p-8 mb-8">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Calcular Minutos</h2>
          <div className="flex gap-2 mb-6">
            <button onClick={() => setMode('somar')} className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-colors ${mode === 'somar' ? 'bg-blue-primary text-white' : 'bg-gray-100 text-body-text'}`}>➕ Somar Minutos</button>
            <button onClick={() => setMode('converter')} className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-colors ${mode === 'converter' ? 'bg-blue-primary text-white' : 'bg-gray-100 text-body-text'}`}>🔄 Converter</button>
          </div>

          {mode === 'somar' && (
            <>
              <div className="space-y-3">
                {entries.map((entry, i) => (
                  <div key={entry.id} className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-body-text w-6">{`${i + 1}.`}</span>
                    <input type="number" value={entry.value} onChange={(e) => { const newEntries = [...entries]; newEntries[i].value = e.target.value; setEntries(newEntries); }} placeholder="Minutos" min="0" className="flex-1 h-12 px-4 border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none" />
                    <span className="text-sm text-body-text">min</span>
                  </div>
                ))}
              </div>
              <button onClick={addEntry} className="mt-3 text-sm text-blue-primary font-semibold hover:underline">+ Adicionar linha</button>
              {sumResult && (
                <div className="mt-6 bg-light-bg border-l-4 border-success-green rounded-lg p-5">
                  <p className="text-sm font-semibold text-body-text uppercase tracking-wide mb-1">Total</p>
                  <p className="text-2xl font-heading font-bold text-dark-text">{sumResult.formatted}</p>
                  <p className="text-sm text-body-text mt-1">{sumResult.total} minutos = {sumResult.decimal} horas decimais</p>
                </div>
              )}
            </>
          )}

          {mode === 'converter' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-body-text mb-2">Valor</label>
                  <input type="number" value={convertVal} onChange={(e) => setConvertVal(e.target.value)} placeholder="Ex: 150" min="0" className="w-full h-12 px-4 border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-body-text mb-2">Conversão</label>
                  <select value={convertDir} onChange={(e) => setConvertDir(e.target.value)} className="w-full h-12 px-4 border-2 border-border-gray rounded-lg focus:border-blue-primary focus:outline-none">
                    <option value="minToHours">Minutos → Horas</option>
                    <option value="hoursToMin">Horas → Minutos</option>
                    <option value="secToMin">Segundos → Minutos</option>
                  </select>
                </div>
              </div>
              {convertResult && (
                <div className="mt-6 bg-light-bg border-l-4 border-success-green rounded-lg p-5">
                  <p className="text-sm font-semibold text-body-text uppercase tracking-wide mb-1">Resultado</p>
                  <p className="text-2xl font-heading font-bold text-dark-text">{convertResult.result}</p>
                  <p className="text-sm text-body-text mt-1">{convertResult.detail}</p>
                </div>
              )}
            </>
          )}
        </div>

        <AdSlot slotId="ad-slot-1" />

        {/* Table */}
        <section className="mb-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Tabela de Conversão: Minutos para Horas</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead><tr className="bg-light-bg"><th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Minutos</th><th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Horas:Minutos</th><th className="text-left p-3 font-semibold text-dark-text border-b border-border-gray">Decimal</th></tr></thead>
              <tbody>
                {conversionTable.map((row, i) => (
                  <tr key={i} className="hover:bg-light-bg"><td className="p-3 border-b border-border-gray font-bold text-dark-text">{`${row.min} min`}</td><td className="p-3 border-b border-border-gray text-body-text">{row.hours}</td><td className="p-3 border-b border-border-gray text-body-text">{row.decimal}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <article className="prose prose-lg max-w-none">
          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">O Que é a Calculadora de Minutos?</h2>
            <p className="text-body-text leading-relaxed mb-4">A <strong>calculadora de minutos</strong> é uma ferramenta online que permite somar múltiplos valores em minutos e converter automaticamente para horas. É especialmente útil para quem precisa somar tempos de atividades, tarefas ou exercícios medidos em minutos.</p>
            <p className="text-body-text leading-relaxed mb-4">Diferente de uma calculadora comum, esta ferramenta usa o sistema sexagesimal — onde 60 minutos equivalem a 1 hora. Então, somar 45 minutos + 30 minutos resulta em 1 hora e 15 minutos (1,25h decimal), e não 75 minutos.</p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-heading font-bold text-dark-text mb-4">Como Converter Minutos em Horas Decimais</h2>
            <p className="text-body-text leading-relaxed mb-4">Para converter minutos para o formato decimal usado em folhas de pagamento, divida os minutos por 60. A fórmula é simples: <strong>Horas decimais = Minutos ÷ 60</strong>.</p>
            <div className="bg-light-bg p-6 rounded-lg mb-4">
              <h3 className="text-xl font-heading font-semibold text-dark-text mb-2">Cuidado com o Erro Mais Comum</h3>
              <p className="text-body-text">Muitas pessoas confundem 30 minutos com 0,30 horas. O correto é <strong>0,50 horas</strong> (30 ÷ 60 = 0,50). Da mesma forma, 15 minutos = 0,25 horas (não 0,15). Esse erro pode causar divergências no cálculo de salários e horas extras.</p>
            </div>
            <p className="text-body-text leading-relaxed">Para cálculos mais complexos envolvendo horas e minutos juntos, use nossa <a href="/calculadora-de-tempo" className="text-blue-primary hover:underline">calculadora de tempo</a>. Para somar horas de trabalho da semana, experimente a <a href="/" className="text-blue-primary hover:underline">calculadora de horas</a>.</p>
          </section>
        </article>

        <AdSlot slotId="ad-slot-2" />

        <section className="my-12">
          <h2 className="text-2xl font-heading font-bold text-dark-text mb-6">Perguntas Frequentes</h2>
          <FAQAccordion faqs={faqs} />
        </section>

        <RelatedTools currentPath="/calculadora-de-minutos" />
        <AdSlot slotId="ad-slot-3" />
      </main>

      <Footer />
    </>
  );
};

export default CalculadoraMinutosPage;
